import { useState, useMemo, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import type { NewsArticle } from '../../mocks/news';
import { getNewsCategories, getNewsList } from '../../services/news';
import NewsHero, { type NewsHeroTab } from './components/NewsHero';
import NewsFeatured from './components/NewsFeatured';
import NewsGrid from './components/NewsGrid';
import { useTranslation } from 'react-i18next';

export default function NewsPage() {
  const { i18n, t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [newsList, setNewsList] = useState<NewsArticle[]>([]);
  const [heroTabs, setHeroTabs] = useState<NewsHeroTab[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadNews() {
      setLoading(true);
      setLoadError(false);
      try {
        const locale = i18n.resolvedLanguage ?? i18n.language;
        const [result, cmsCats] = await Promise.all([
          getNewsList({ category: 'all', locale }),
          getNewsCategories(locale),
        ]);
        if (!mounted) return;

        setNewsList(result.items);

        const slugIcon = (key: string) => {
          if (key === 'product') return 'ri-rocket-line';
          if (key === 'industry') return 'ri-global-line';
          return 'ri-folder-line';
        };

        setHeroTabs([
          { key: 'all', label: t('newsPage.categories.all'), icon: 'ri-apps-line' },
          ...cmsCats.map((c) => ({
            key: c.key,
            label: c.label,
            icon: slugIcon(c.key),
          })),
        ]);
        setLoading(false);
      } catch (error) {
        console.error('failed to load news list:', error);
        if (mounted) {
          setNewsList([]);
          setHeroTabs([]);
          setLoadError(true);
          setLoading(false);
        }
      }
    }

    void loadNews();
    return () => {
      mounted = false;
    };
  }, [i18n.resolvedLanguage, i18n.language, t]);

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return newsList;
    return newsList.filter((n) => n.category === activeCategory);
  }, [activeCategory, newsList]);
  const featured = newsList[0];

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
  };

  return (
    <Layout>
      <NewsHero
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        totalCount={newsList.length}
        tabs={
          heroTabs.length > 0
            ? heroTabs
            : [{ key: 'all', label: t('newsPage.categories.all'), icon: 'ri-apps-line' }]
        }
      />
      {loading && (
        <section className="bg-white py-16">
          <div className="max-w-[1400px] mx-auto px-6 text-sm text-gray-400">加载新闻中...</div>
        </section>
      )}
      {!loading && loadError && (
        <section className="bg-white py-16">
          <div className="max-w-[1400px] mx-auto px-6 text-sm text-red-500">新闻加载失败，请刷新重试。</div>
        </section>
      )}
      {!loading && !loadError && activeCategory === 'all' && featured && <NewsFeatured article={featured} />}
      {!loading && !loadError && <NewsGrid articles={filtered} />}
    </Layout>
  );
}
